/**
 * ResilientSuspenseFallback
 * ──────────────────────────────────────────────────────────────────────
 * Staged Suspense fallback that distinguishes "healthy-but-slow" from
 * "actually broken" so users on real residential connections never see
 * a recoverable-failure UI for a load that is just taking a while.
 *
 * Three render stages, monotonically ordered:
 *
 *   1. `idle`      — default. Renders the existing lowercase
 *                    "loading…" copy users are used to. The fast,
 *                    happy-path render (the lazy chunk almost always
 *                    resolves before any timer elapses).
 *
 *   2. `softHint`  — after `softHintMs` (default 6 s). Swap copy from
 *                    "loading…" to "still loading…". Same visual style.
 *                    NO refresh button. This is a low-urgency progress
 *                    signal, NOT a failure UI. Users on slow networks
 *                    or large bundles see this briefly and the chunk
 *                    finishes mounting in the next 1–4 s.
 *
 *   3. `stuck`     — after `terminalMs` (default 30 s). ONLY here do we
 *                    surface the recoverable
 *                    "this page is taking longer than expected to load.
 *                    refresh" UI. By 30 s a real chunk-load failure
 *                    (404, ChunkLoadError, ERR_BLOCKED_BY_CLIENT) or a
 *                    hard hang (DNS/TLS, throttled tab) is the only
 *                    realistic explanation.
 *
 * Why this exists
 * ──────────────────────────────────────────────────────────────────────
 * On 2026-05-10, Miles screenshotted the failure overlay on
 * `prest0.ai/desktop` and reported "this happens a lot for some reason.
 * It happens on most pages." Diagnosis: the previous version of this
 * component had a single 4 s → terminal-refresh-button transition,
 * which conflated "healthy-but-slow" (5+ MB main bundle still parsing
 * on a real residential connection) with "bundle failed". Headless
 * Chromium reproduced the false-fire 100% on a 12 s wait against the
 * live deploy. ALL chunks returned 200 OK; the page just hit the 4 s
 * budget while React was mid-mount of the lazy route.
 *
 * The earlier 2026-05-09 demo-day stall (Firestore real-time
 * `Listen/channel` long-poll blocked by a browser extension with
 * `net::ERR_BLOCKED_BY_CLIENT`) is still covered: at 30 s the stuck
 * state surfaces a refresh button. The staged design strictly improves
 * on the previous one — every previous failure case still surfaces, just
 * later and only after evidence accumulates.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  FallbackStage,
  LOADING_GATE_SOFT_HINT_MS,
  LOADING_GATE_TERMINAL_TIMEOUT_MS,
} from '../utils/loadingGate';
import BrandedTransition from './common/BrandedTransition';
import { semanticDark } from '../styles/semanticTokens.generated';
import type { PreviewTheme } from '../design/previewTheme';

interface ResilientSuspenseFallbackProps {
  /** Identifier surfaced in console warnings. */
  gateName?: string;
  /**
   * Override the soft-hint budget (the elapsed time at which copy swaps
   * from `loading…` to `still loading…`). Defaults to 6000 ms.
   */
  softHintMs?: number;
  /**
   * Override the terminal-fallback budget (the elapsed time at which
   * the recoverable refresh UI is shown). Defaults to 30000 ms.
   */
  terminalMs?: number;
  /**
   * Back-compat alias for `terminalMs`. Older call sites passed
   * `timeoutMs={LOADING_GATE_DEFAULT_TIMEOUT_MS}` (4000 ms) expecting the
   * old single-timer terminal behaviour. Honoured if `terminalMs` is not
   * also set, but new call sites should use `terminalMs` directly.
   */
  timeoutMs?: number;
  /**
   * The light of the page this screen stands in front of (D-92 part 3; the
   * justice page's gate, through ./routing/GuardLoading.tsx): 'dark' draws
   * every stage at dusk. Left out, noon as always.
   */
  light?: PreviewTheme | null;
}

const ResilientSuspenseFallback: React.FC<ResilientSuspenseFallbackProps> = ({
  gateName = 'route-suspense',
  softHintMs = LOADING_GATE_SOFT_HINT_MS,
  terminalMs,
  timeoutMs,
  light,
}) => {
  // Resolve the terminal budget: explicit `terminalMs` wins, then the
  // back-compat `timeoutMs` alias, then the default.
  const resolvedTerminalMs =
    typeof terminalMs === 'number'
      ? terminalMs
      : typeof timeoutMs === 'number'
        ? timeoutMs
        : LOADING_GATE_TERMINAL_TIMEOUT_MS;

  const [stage, setStage] = useState<FallbackStage>('idle');
  // Refs so the cleanup effect does not need stage in its dep array.
  const softHintHandleRef = useRef<number | null>(null);
  const terminalHandleRef = useRef<number | null>(null);

  useEffect(() => {
    // Schedule the soft-hint transition.
    if (softHintMs < resolvedTerminalMs) {
      softHintHandleRef.current = window.setTimeout(() => {
        setStage((prev) => (prev === 'idle' ? 'softHint' : prev));
      }, softHintMs);
    }
    // Schedule the terminal transition. Logged at warn-level so it shows
    // up in production DevTools and any error-monitoring sink.
    terminalHandleRef.current = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn(
        `[loadingGate:watchdog] gate="${gateName}" suspense fallback still ` +
          `rendering after ${resolvedTerminalMs}ms. The lazy chunk likely ` +
          'failed to load or a downstream gate is hung (check Network tab ' +
          'for blocked /static/js/*.chunk.js requests, including ' +
          'net::ERR_BLOCKED_BY_CLIENT from browser extensions). Showing ' +
          'recoverable refresh UI so the user can recover.',
      );
      setStage('stuck');
    }, resolvedTerminalMs);

    return () => {
      if (softHintHandleRef.current !== null) {
        window.clearTimeout(softHintHandleRef.current);
        softHintHandleRef.current = null;
      }
      if (terminalHandleRef.current !== null) {
        window.clearTimeout(terminalHandleRef.current);
        terminalHandleRef.current = null;
      }
    };
  }, [gateName, softHintMs, resolvedTerminalMs]);

  if (stage === 'stuck') {
    return (
      <div
        role="status"
        aria-live="polite"
        data-stage="stuck"
        style={{
          padding: '2rem',
          textAlign: 'center',
          // at dusk the ground under it is the night's (src/styles/theme.ts): the dusk ink
          color: light === 'dark' ? semanticDark['ink-2'] : '#444',
          fontFamily:
            "'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div style={{ marginBottom: '0.75rem' }}>
          this page is taking longer than expected to load.
        </div>
        <button
          type="button"
          onClick={() => {
            try {
              window.location.reload();
            } catch {
              /* no-op */
            }
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.95rem',
            background: 'var(--brand-gold-alt)',
            color: '#0b0c0f',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          refresh
        </button>
      </div>
    );
  }

  // Branded idle/soft-hint states (2026-08-09 owner directive): this
  // fallback is the cold-cache entry to every lazy route — including /auth
  // right after the user commits to creating an account — so it must read
  // as the product working, not a bare tiny "loading…" line. The 6s/30s
  // escalation logic above is unchanged. BrandedTransition is deliberately
  // dependency-light (no context/router hooks), safe inside route Suspense.
  if (stage === 'softHint') {
    return (
      <div data-stage="soft-hint">
        <BrandedTransition
          variant="page"
          title="still loading…"
          subtitle="hang tight — this is taking a little longer than usual."
          light={light}
        />
      </div>
    );
  }

  return (
    <div data-stage="idle">
      <BrandedTransition variant="page" title="loading…" light={light} />
    </div>
  );
};

export default ResilientSuspenseFallback;
